import moment from "moment";
import { toPaise } from "../../AddMoneyToUser/addMoneyUtils";

/**
 * RoadZen Wallet page — the words, defaults and checks, tested on their own (roadzenWalletUtils.test.js). The server
 * sends money in whole paise and does every sum; this file only formats and checks what is typed.
 */

/** "₹1,150.00" from paise; "—" when RoadZen gave nothing. */
export const rupeesText = (paise) =>
  paise === null || paise === undefined || !Number.isFinite(Number(paise))
    ? "—"
    : `₹${(Number(paise) / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const whenText = (at) => (at && moment(at).isValid() ? moment(at).format("DD-MM-YYYY hh:mm A") : "—");

/** The dropdown opens on the active RoadZen user, else the newest one. */
export const defaultUserId = (users) => {
  const list = users || [];
  const active = list.find((u) => u.isActive);
  return active ? active.id : list.length ? list[0].id : null;
};

export const userOptionLabel = (u) => `${u.name} · ${u.code} · ${u.isActive ? "Active" : "Inactive"}`;

export const MODES = {
  TEST_CREDIT: { label: "Test credit (sandbox)", minPaise: 100, maxPaise: 10000000, hint: "Test money, added at once. Only on the RoadZen sandbox." },
  PAYMENT_LINK: { label: "Payment link", minPaise: 10000, maxPaise: 10000000, hint: "A RoadZen link to pay by UPI / card. The money is added when it is paid. Valid 7 days." },
};
export const NOTE_MAX = 60;

/** "" when the typed amount may be sent for this choice, else the line to show under the box (same rules as the server). */
export const amountProblem = (mode, value) => {
  const text = String(value || "").trim().replace(/\.$/, "");
  if (!text) return "Enter the amount";
  if (/^\d+\.\d{3,}$/.test(text)) return "At most 2 decimals";
  const paise = toPaise(text);
  if (Number.isNaN(paise)) return "Enter the amount in rupees";
  const { minPaise, maxPaise } = MODES[mode] || MODES.PAYMENT_LINK;
  if (paise < minPaise || paise > maxPaise) return `Enter an amount from ${rupeesText(minPaise)} to ${rupeesText(maxPaise)}`;
  return "";
};

/** The yellow line when the figures do not add up (null when they do, or when they were not all read). */
export const mismatchWarning = (summary) => {
  if (!summary) return null;
  if (summary.matches === null && summary.complete === false) {
    return `The statement has more than ${summary.rowCount} rows, so Total Added and Total Used are not checked against the balance.`;
  }
  if (summary.matches !== false) return null;
  const parts = [];
  if (summary.differencePaise) {
    parts.push(`The balance ${rupeesText(summary.balancePaise)} is not Total Added − Total Used (difference ${rupeesText(Math.abs(summary.differencePaise))}).`);
  }
  if (summary.firstBreak) {
    parts.push(`The statement does not follow on at the row of ${whenText(summary.firstBreak.at)}.`);
  }
  parts.push("Nothing was changed. Please tell RoadZen with this user's code.");
  return parts.join(" ");
};

/** A statement row's Transaction Type, like the Brisk ledger's: Policy (debit), Refund (credit for a policy), Test credit, Top-up. */
export const transactionTypeOf = (row = {}) => {
  if (row.type === "DEBIT") return "Policy";
  if (row.type !== "CREDIT") return row.type || "—";
  if (row.policyId || row.certificateNumber) return "Refund";
  if (/test credit/i.test(row.note || "")) return "Test credit";
  return "Top-up";
};

/** The Ledger Check card: do RoadZen's balance and its statement agree? Green / red only because they mean something. */
export const ledgerCheck = (summary) => {
  if (!summary) return { value: "—", hint: "", accent: "#98A2B3" };
  if (summary.matches === true) return { value: "Matches", hint: `${summary.rowCount} rows checked to the paisa`, accent: "#067647" };
  if (summary.matches === false) {
    return { value: "Mismatch", hint: summary.differencePaise ? `Off by ${rupeesText(Math.abs(summary.differencePaise))}` : "A row does not follow on", accent: "#B42318" };
  }
  return { value: "Not checked", hint: `More than ${summary.rowCount} rows`, accent: "#98A2B3" };
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
/** The small line under Total Added / Total Used. */
export const countHint = (n, one, many) => (typeof n === "number" ? plural(n, one, many) : "");

export const TOPUP_STATUS = {
  PENDING: { label: "Not paid yet", colour: "#B54708" },
  PAID: { label: "Paid", colour: "green" },
  FAILED: { label: "Failed", colour: "red" },
  EXPIRED: { label: "Expired", colour: "grey" },
};
export const topupStatusLabel = (status) => (TOPUP_STATUS[status] || {}).label || status || "—";
export const topupStatusColour = (status) => (TOPUP_STATUS[status] || {}).colour || "inherit";

/**
 * Zen Wallet (owner, 2026-11-04): money RoadZen confirmed also goes to an agency's Company Wallet "RoadZen". The server
 * says what happened (`zen` = { state, text, short }); the page only colours it. A problem is never shown as success.
 */
export const ZEN_PROBLEMS = ["FAILED", "STUCK", "NOT_CONFIRMED", "MISMATCH"];
const ZEN_COLOURS = { DONE: "green", WAITING: "#B54708", CREDITING: "#B54708", NOT_PAID: "grey" };
export const zenProblem = (zen) => Boolean(zen && ZEN_PROBLEMS.includes(zen.state));
export const zenColour = (zen) => (zenProblem(zen) ? "red" : ZEN_COLOURS[zen && zen.state] || "inherit");

/** The super admin's agency choice: every client but the super admin itself (the server refuses it too). */
export const agencyOptionsOf = (clients) =>
  (clients || [])
    .filter((c) => c && c._id && String(c.roleId || "").trim().toUpperCase() !== "SUPER_ADMIN")
    .map((c) => ({ value: String(c._id), label: c.name || c.client || c.email || String(c._id) }))
    .sort((a, b) => a.label.localeCompare(b.label));

/** Only a RoadZen https address is ever opened or copied. */
export const isSafeLink = (url) => /^https:\/\/[^\s/]+\.roadzen\.ai\//i.test(String(url || ""));
